


import RootApiService from '@/services/api/RootApiService';

export const PublicSearchResultType = {
  EVENT: 'event',
  CLUB: 'club',
  CITY: 'city',
  PLAYER: 'player',
};

/**
 * Формирует единый объект результата для публичной поисковой карточки.
 * Компонент карточки получает уже готовые строки и не знает о структуре исходных данных.
 * @param {'event'|'club'|'city'|'player'|string} type
 * @param {Object} source
 * @returns {Object}
 */
export function formatPublicSearchResult(type, source = {}) {
  const resultType = type || source.type || '';

  switch (resultType) {
    case PublicSearchResultType.EVENT:
      return {
        type: PublicSearchResultType.EVENT,
        typeLabelKey: 'search.tournament',
        title: source.title,
        meta: `${source.club} · ${source.city}`,
        detail: `${source.date} · ${source.time}`,
        actionKey: 'search.openTournament',
        icon: 'bi bi-calendar2-week',
        route: source.route || { name: 'AllClubs' },
        searchText: `${source.title} ${source.club} ${source.city} ${source.control} ${source.system}`,
        clubName: source.club || '',
        clubLocation: source.city || '',
        tournament: source.tournament || {
          place: source.place || null,
          date: source.date || null,
          time: source.time || null,
          system: source.systemType || source.system || null,
          status: source.status || 'created',
          competitionFormat: source.competitionFormat || 'individual',
          rated: Boolean(source.rated),
          chessVariant: source.chessVariant || 'classic',
          teamMode: source.teamMode || null,
          control: {
            type: source.controlType || null,
            label: source.control || '',
            timeMinutes: null,
            increment: null,
          },
          badge: source.badge || '',
        },
      };

    case PublicSearchResultType.CLUB:
      const location = [source.city, source.country].filter(Boolean).join(', ');
      return {
        type: PublicSearchResultType.CLUB,
        typeLabelKey: 'search.club',
        title: source.name,
        meta: location,
        tournamentsCount: Number(source.tournamentsCount || 0),
        playersCount: Number(source.playersCount || 0),
        actionKey: 'search.openClub',
        icon: 'bi bi-shop-window',
        route: source.route || (source.slug ? { name: 'ClubHome', params: { clubSlug: source.slug } } : { name: 'AllClubs' }),
        searchText: `${source.name} ${source.city || ''} ${source.country || ''} ${source.slug || ''}`,
      };

    case PublicSearchResultType.CITY:
      return {
        type: PublicSearchResultType.CITY,
        typeLabelKey: 'search.city',
        title: source.name,
        metaKey: 'search.activeChessCity',
        clubsCount: Number(source.clubs || 0),
        tournamentsCount: Number(source.tournaments || 0),
        actionKey: 'search.viewCity',
        icon: 'bi bi-geo-alt',
        route: source.route || { name: 'AllClubs' },
        searchText: `${source.name} город клубы турниры`,
      };

    default:
      return {
        type: resultType,
        typeLabel: source.typeLabel || '',
        typeLabelKey: source.typeLabelKey || 'search.result',
        title: source.title || source.name || '',
        meta: source.meta || '',
        detail: source.detail || '',
        action: source.action || '',
        actionKey: source.actionKey || 'search.open',
        icon: source.icon || 'bi bi-search',
        route: source.route || { name: 'Home' },
        searchText: source.searchText || `${source.title || source.name || ''} ${source.meta || ''}`,
      };
  }
}

export const PUBLIC_SEARCH_FILTERS = [
  {
    key: 'event',
    labelKey: 'search.tournaments',
    icon: 'bi bi-trophy',
  },
  {
    key: 'club',
    labelKey: 'search.clubs',
    icon: 'bi bi-shop-window',
  },
  {
    key: 'city',
    labelKey: 'search.cities',
    icon: 'bi bi-geo-alt',
  },
];

export function buildPublicSearchResponse(items = [], options = {}) {
  const page = Math.max(1, Number(options.page || 1));
  const pageSize = Math.max(1, Number(options.pageSize || items.length || 1));
  const start = (page - 1) * pageSize;
  const data = items.slice(start, start + pageSize);
  const counts = items.reduce((acc, item) => ({
    ...acc,
    [item.type]: (acc[item.type] || 0) + 1,
  }), {});
  const total = items.length;

  return {
    data,
    counts,
    total,
    pagination: {
      page,
      pageSize,
      pageCount: Math.max(1, Math.ceil(total / pageSize)),
      total,
    },
  };
}

export async function searchPublicItemsApi(options = {}) {
  const params = {
    q: options.query || '',
    page: options.page || 1,
    pageSize: options.pageSize || 12,
    scope: options.scope || 'future',
    language: options.language || 'en',
    cityPlaceId: options.cityPlaceId || undefined,
    countryCode: options.countryCode || undefined,
  };

  if (Array.isArray(options.types) && options.types.length) {
    params.types = options.types.join(',');
  }

  const res = await RootApiService.api.get('/public/search', {
    params,
    skipToken: true,
  });

  return {
    data: Array.isArray(res?.data?.data) ? res.data.data : [],
    counts: res?.data?.counts || {},
    total: Number(res?.data?.total || 0),
    pagination: res?.data?.pagination || {
      page: params.page,
      pageSize: params.pageSize,
      pageCount: 1,
      total: 0,
    },
  };
}
